<template>
  <div id="app">
    <router-view></router-view>

    <div class="overlay" v-if="loading">
      <div class="outer">
        <div class="middle">
          <div class="inner">
            <div class="loading loading-lg"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// router import needed for <router-view></router-view>
import '@/router'
import 'firebase/database'
import '@mapit/muvue/dist/muvue.css'

import * as Sentry from '@sentry/browser'
import firebase from 'firebase/app'
import moment from 'moment'
import {mapGetters} from 'vuex'

import {authAdapter} from '@/services/auth-adapter'
import {setupAxiosInterceptors} from '@/services/axios-interceptors'
import {destroyInactivityTracker, initInactivityTracker} from '@/services/inactivity-tracker'
import {isDevelopment} from '@/utils/env'

/**
 * Firebase config
 */
const config = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.VUE_APP_FIREBASE_DB_URL,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGE_SENDIR_ID,
}

/**
 * Firebase News Realtime db config
 */
var configNews = {
  apiKey: 'AIzaSyAaig0-TEF5iyvrrVRaN8rSi5UFcnNPDpw',
  authDomain: 'mapitnews.firebaseapp.com',
  databaseURL: 'https://mapitnews.firebaseio.com',
  projectId: 'mapitnews',
  storageBucket: 'mapitnews.appspot.com',
  messagingSenderId: '517171381784',
}

//Init firebse connections
firebase.initializeApp(config)

/**
 * The custom HTML `<App>` component.
 *
 * @author Julià Soler
 * @license Mapit IoT S.L.
 */
export default {
  name: 'app',
  data() {
    return {
      inactivityTracker: null,
    }
  },
  created() {
    this.loadCache()
    this.setupInactivityTracking()
  },
  beforeDestroy() {
    this.cleanupInactivityTracking()
  },
  methods: {
    /**
     * Setup inactivity tracking for auto-logout
     * Honda Cybersecurity Compliance: 2-hour inactivity timeout
     * Only enabled when Panex is supported
     */
    setupInactivityTracking() {
      // Setup axios interceptors to track API activity
      setupAxiosInterceptors()

      // Development feature flag
      // In non-production, allow debug timeout override
      let timeoutMs = 2 * 60 * 60 * 1000 // Default: 2 hours
      if (isDevelopment()) {
        const debugTimeout = this.$store.getters.getDebugInactivityTimeout
        if (debugTimeout) {
          timeoutMs = debugTimeout
          console.log(`🐛 Debug mode: Using custom inactivity timeout of ${timeoutMs}ms`)
        }
      }

      // Initialize inactivity tracker (but don't start it yet)
      this.inactivityTracker = initInactivityTracker({
        timeoutMs,
        onTimeout: async () => {
          console.log('User inactive - logging out')

          try {
            // Sign out using authAdapter
            await authAdapter.signOut()

            // Dispatch logout action to clear Vuex state
            await this.$store.dispatch('logout')

            // Show alert message (permanent until user logs in)
            this.$store.dispatch('setAlertResult', {
              type: 'warning',
              stringId: this.$t('auth.sessionExpired'),
              open: true,
              timeout: -1,
            })

            // Redirect to login
            this.$router.push({name: 'Login'})
          } catch (error) {
            console.error('Error during auto-logout:', error)
            // Force redirect to login even if logout fails
            this.$router.push({name: 'Login'})
          }
        },
      })
    },

    /**
     * Start inactivity tracking when user is authenticated
     */
    startInactivityTracking() {
      if (this.inactivityTracker) {
        this.inactivityTracker.start()
      }
    },

    /**
     * Stop inactivity tracking
     */
    stopInactivityTracking() {
      if (this.inactivityTracker) {
        this.inactivityTracker.stop()
      }
    },

    /**
     * Cleanup inactivity tracking on app destroy
     */
    cleanupInactivityTracking() {
      destroyInactivityTracker()
    },

    /**
     * Load firebase storedcache config
     */
    loadCache() {
      /**
       * Update store
       * @param {string} action - Action name
       * @param {object} value - Firebase Config
       */
      this.$store.dispatch('setFirebase', firebase)

      if (['staging', 'production'].includes(process.env.VUE_APP_ENV_MODE)) {
        const secondary = firebase.initializeApp(configNews, 'secondary')
        this.$store.dispatch('setFirebaseNews', secondary)
      }
    },
  },
  watch: {
    group: {
      immediate: true,
      handler(val) {
        // If user has a group (logged in), use group locale
        // If no group (logged out), reset to browser language
        const browserLocale = navigator.language?.substring(0, 2).toLowerCase() || 'es'
        this.$i18n.locale = val?.locale?.substring(0, 2).toLowerCase() || browserLocale
        moment.locale(val?.locale || navigator.language || 'es-ES')

        if (this.$gtm.enabled() && val?.id) {
          if (!window.dataLayer.find(item => item.dealerId === val?.id)) {
            window.dataLayer?.push({
              event: 'gtm.init',
              dealerName: val?.name,
              dealerId: val?.id,
              branch: val?.branch,
              version: process.env.VUE_APP_GIT_SEMVER,
            })
          }
        }

        Sentry.setTags({
          dealerId: val?.id,
          dealerName: val?.name,
          branch: val?.branch,
          version: process.env.VUE_APP_GIT_SEMVER,
        })
      },
    },
    $route: {
      immediate: true,
      handler(to) {
        if (to.meta?.title) {
          document.title = this.$t('title.template', {
            title: this.$t(to.meta.title),
            brand: this.$t('title.brand'),
          })
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      group: 'getGroup',
    }),
    /**
     * Checks login status
     * @returns {boolean} true/false
     */
    loading() {
      return this.$store.getters.getLoading
    },
  },
}
</script>

<style src="@/styles/app.less" lang="less"></style>
<style lang="scss">
.multiple .v-select__selections {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.icon-filter-status:before {
  content: '\e02b';
}

.v-data-table-header {
  .v-data-table-header__icon:before {
    margin: auto 0 auto 6px;
  }

  [aria-sort='descending'] {
    .v-data-table-header__icon:before {
      content: '\f0dd';
    }
  }

  [aria-sort='ascending'] {
    .v-data-table-header__icon:before {
      content: '\f0de';
    }
  }

  [aria-sort='none'] {
    .v-data-table-header__icon:before {
      content: '\f0dc';
    }
  }
}

.list-menu-actions {
  min-width: 200px !important;

  .v-list-item__title {
    font-family: 'ProximaNovaRegular' !important;
    font-size: 14px !important;
  }
}

.v-list-item__action {
  margin-right: 12px !important;
}

.icon-flag-on:before {
  cursor: pointer;
  color: var(--v-secondary-base);
  content: '\e03d';
}

.icon-flag-off:before {
  cursor: pointer;
  color: var(--v-gray-lighten2);
  content: '\e03d';
}

.v-data-table-header tr th {
  margin-top: 20px;
}

.v-data-table-header tr th span,
.v-data-table-header tr th i {
  display: inline !important;
}

#app {
  .v-btn {
    border-radius: 4px !important;
    box-shadow: none;
  }

  .v-btn:focus {
    outline: 3px solid rgba(0, 137, 222, 0.8) !important;
    border: none !important;
    transition: outline 80ms ease-in-out !important;
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 5s ease-in-out;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
